import { ReactComponent as ZoomIcon } from '@/assets/icons/zoom-input.svg';
import type { FormItemProps } from 'antd';
import i18n from 'i18next';
import { type FC, type FocusEventHandler, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setAppLoader } from '../../../redux/common/CommonActions';
import { getZipCodeDetailsEffect } from '../../../redux/shipment/ShipmentEffects';
import PostalSearchModal from '../../screens/common/PostalSearchModal/PostalSearchModal';
import type { PostalInfo } from '../../screens/common/types';
import IconButton from '../Buttons/IconButton';
import SvgConverter from '../Common/SvgConverter';
import FormInput from '../Inputs/FormInput';

export interface PostalCodeInputValues {
  state: string;
  city: string;
  countryCode: string;
  zip: string;
}

interface PostalCodeInputProps {
  placeholder?: string;
  label?: string;
  rules?: FormItemProps['rules'];
  name?: string;
  editMode: boolean;
  countryCode?: string;
  handleZipDetails?: (values: PostalCodeInputValues) => void;
  margin?: string;
  mode?: string;
  disableValidation?: boolean;
  maxLength?: number;
  tabIndex?: number;
  labelForTestId?: string;
}

const PostalCodeInput: FC<PostalCodeInputProps> = ({
  placeholder = i18n.t('translation:default.fields.postalCode.placeholder'),
  label = i18n.t('translation:default.fields.postalCode.label'),
  rules = [],
  name,
  editMode,
  countryCode,
  handleZipDetails,
  margin,
  mode = '',
  disableValidation = false,
  maxLength,
  tabIndex,
  labelForTestId,
}) => {
  const [modal, setModal] = useState(false);
  const dispatch = useDispatch();

  const handleBlur: FocusEventHandler<HTMLInputElement> = async ({ target: { value } }) => {
    if (countryCode && value && handleZipDetails) {
      try {
        dispatch(setAppLoader(true));
        const payload = { CountryCode: countryCode, Zip: value };
        const { data } = await getZipCodeDetailsEffect(payload);
        dispatch(setAppLoader(false));
        handleZipDetails(data);
      } catch {
        dispatch(setAppLoader(false));
      }
    }
  };

  const handleSelect = (data: PostalInfo) => {
    handleZipDetails?.({
      state: data.state.trim(),
      city: data.city.trim(),
      countryCode: data.country.trim(),
      zip: data.zip.trim(),
    });
    setModal(false);
  };

  return (
    <>
      <FormInput
        placeholder={placeholder}
        label={label}
        margin={margin}
        rules={rules}
        name={name}
        editMode={editMode}
        onBlur={handleBlur}
        mode={mode}
        addonAfter={
          <IconButton onClick={() => setModal(true)}>
            <SvgConverter Icon={ZoomIcon} color="muted" />
          </IconButton>
        }
        disableValidation={disableValidation}
        maxLength={maxLength}
        tabIndex={tabIndex}
      />
      <PostalSearchModal
        open={modal}
        countryCode={countryCode}
        handleSelect={handleSelect}
        handleClose={() => setModal(false)}
        labelForTestId={labelForTestId}
      />
    </>
  );
};

export default PostalCodeInput;
